import { DbHelpers } from '@Utils/db-driver-helpers';
import { TFunc } from '../../i18n/types';
import * as yup from 'yup';

const testPortRange = (value: string | null | undefined) => {
	if (!value) return true;
	const portNum = parseInt(value ?? '');
	return !isNaN(portNum) && portNum >= 0 && portNum <= 65535;
};

export const SystemConnectionSchema = (t: TFunc) => {
	const VALIDATOR_PORT = yup
		.string()
		.required(t('Port is required.'))
		.matches(/^\d+$/, t('Port must be a number.'))
		.typeError(t('Port must be a number.'))
		.test(
			'port',
			t('Port must be in the range between 0 and 65535.'),
			value => testPortRange(value),
		);
	return yup.object().shape({
		showODBCForm: yup.string(),

		connectionName: yup
			.string()
			.required(t('Connection Name is required.'))
			.typeError(t('Connection Name must be a string.')),
		connectionType: yup
			.string()
			.required(t('Connection Type is required.'))
			.typeError(t('Connection Type must be a string.')),
		serverType: yup
			.string()
			.when('connectionType', {
				is: 'ODBC',
				then: yup.string().required(t('Server Type is required.')),
			})
			.typeError(t('Server Type must be a string.')),
		serverAddress: yup
			.string()
			.required(t('Server Address is required.'))
			.typeError(t('Server Address must be a string.')),
		username: yup
			.string()
			.required(t('Username is required.'))
			.typeError(t('Username must be a string.')),
		password: yup
			.string()
			.required(t('Password is required.'))
			.typeError(t('Password must be a string.')),
		database: yup.string().typeError(t('Database must be a string.')),
		port: yup.string().when('connectionType', {
			is: (value: string) => ['ODBC', 'OPC-UA'].some(e => e === value),
			then: VALIDATOR_PORT,
			otherwise: yup.string().when('port', {
				is: (value: string) =>
					!!value && !!value.length && value !== 'NaN',
				then: VALIDATOR_PORT,
				otherwise: yup.string().optional(),
			}),
		}),
		connectionString: yup
			.string()
			.when('connectionType', {
				is: 'ODBC',
				then: yup
					.string()
					.test(
						'connection string',
						t('Connection String must contain a DRIVER'),
						value =>
							!!value?.match(
								/(^|;)\s*(driver|dsn)\s*=\s*[^\s;]+\s*;?/i,
							),
					)
					.required(t('Connection String is required.')),
			})
			.when('connectionType', {
				is: 'OLEDB',
				then: yup
					.string()
					.test(
						'connection string',
						t('Connection String must contain a Provider'),
						value =>
							!!value?.match(
								/(^|;)\s*(provider|dsn)\s*=\s*[^\s;]+\s*;?/i,
							),
					)
					.required(t('Connection String is required.')),
			})
			.typeError(t('Connection String must be a string.')),
		databaseTimezone: yup
			.string()
			.required(t('Database Timezone is required.'))
			.typeError(t('Database Timezone must be a string.')),
		sampleDurationSeconds: yup
			.number()
			.typeError(t('Sample Duration must be an integer.'))
			.required(t('Sample Duration is required.'))
			.min(60, t('Sample Duration must be integer >= 60.')),
		queryLagMinutes: yup
			.number()
			.when('connectionType', {
				is: 'ODBC',
				then: yup.number().required(t('Query Lag is required.')),
			})
			.min(0, t('Query Lag must be integer >= 0.'))
			.typeError(t('Query Lag must be an integer.')),
		queryGroupSize: yup
			.number()
			.when('serverType', {
				is: value => DbHelpers.isOfType('iHistorian', value),
				then: yup
					.number()
					// eslint-disable-next-line prettier/prettier
					.max(195, t('Query Group Size can\'t be greater than 195'))
					.required(t('Simulation Subtraction Days is required.')),
			})
			.min(0, t('Query Group Size must be integer.'))
			.typeError(t('Query Group Size must be an integer.'))
			.required(t('Query Group Size is required.')),
		caseSensitiveSensorId: yup
			.boolean()
			.when('connectionType', {
				is: 'ODBC',
				then: yup
					.boolean()
					.required(t('Sensor ID Case Sensitivity is required.')),
			})
			.typeError(t('Sensor ID Case Sensitivity must be a boolean.')),
		simulationSubtractionDays: yup
			.number()
			.when('connectionType', {
				is: 'ODBC',
				then: yup
					.number()
					.required(t('Simulation Subtraction Days is required.')),
			})
			.min(
				0,
				t('Simulation Subtraction Days cannot be a negative value.'),
			)
			.typeError(t('Simulation Subtraction Days must be an integer.')),
		resourcePath: yup
			.string()
			.when('connectionType', {
				is: 'OPC-UA',
				then: yup.string().optional(),
			})
			.typeError(t('Resource Path must be a string.')),
		dateTimeFormat: yup
			.string()
			.when('connectionType', {
				is: 'ODBC',
				then: yup.string().required(t('Date Format is required.')),
			})
			.typeError(t('Date Format must be a string.')),
		quoteFormat: yup
			.string()
			.when('connectionType', {
				is: 'ODBC',
				then: yup.string().required(t('Quote Format is required.')),
			})
			.typeError(t('Quote Format must be a string.')),
		timestampConversion: yup
			.string()
			.when('connectionType', {
				is: 'ODBC',
				then: yup
					.string()
					.required(t('Timestamp Conversion is required.')),
			})
			.typeError(t('Timestamp Conversion must be a string.')),
		tagFormat: yup
			.string()
			.when('connectionType', {
				is: 'ODBC',
				then: yup.string().required(t('Tag Field Format is required.')),
			})
			.typeError(t('Tag Field Format must be a string.')),
		queryPrefix: yup
			.string()
			.typeError(t('SQL SET Commands must be a string.'))
			.test(
				'queryPrefix',
				t('Invalid SQL SET Commands value.'),
				(value: unknown) => !/select|;/gi.test(`${value}`),
			),
	});
};
